import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { differentials } from "@/data/services";

export const DifferentialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="diferenciais"
      ref={ref}
      className="relative py-16 md:py-32 bg-white overflow-hidden scroll-mt-20"
      data-testid="differentials-section"
    >
      {/* Gradiente de descida sutil para suavizar a transição com a seção anterior */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#192338]/5 via-transparent to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-16"
        >
          {/* Subtítulo: Tipografia com tracking largo para o padrão institucional */}
          <span className="text-[#DDC385] text-xs md:text-sm uppercase tracking-[0.4em] font-semibold mb-4 block">
            Por que nos escolher
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#192338] leading-tight">
            Nossos Diferenciais
          </h2>
          {/* Linha de gradiente centralizada (h-px) para foco visual */}
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-[#DDC385] to-transparent mx-auto mt-6 opacity-60" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentials.map((diff, index) => (
            <motion.div
              key={diff.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="group relative bg-[#192338]/[0.02] p-6 md:p-8 border border-[#192338]/5 hover:border-[#DDC385]/40 transition-all duration-500 overflow-hidden"
              data-testid={`differential-card-${index}`}
            >
              {/* Efeito Glow interno: Ativado no hover com baixa opacidade */}
              <div className="absolute inset-0 bg-[#DDC385]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" />

              <div className="relative z-10">
                {/* Ícone: Moldura com transição de cor sólida no hover */}
                <div className="w-12 h-12 md:w-14 md:h-14 border border-[#DDC385]/40 flex items-center justify-center mb-6 group-hover:bg-[#192338] group-hover:border-[#192338] transition-all duration-500">
                  <diff.icon className="w-5 h-5 md:w-6 md:h-6 text-[#DDC385] transition-colors duration-300" />
                </div>

                <h3 className="font-heading text-base md:text-lg font-bold text-[#192338] mb-3">
                  {diff.title}
                </h3>

                <p className="text-slate-600 text-sm leading-relaxed font-light">
                  {diff.description}
                </p>
              </div>

              {/* Linha final do card: Gradiente lateral que expande no hover */}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-gradient-to-r from-transparent via-[#DDC385] to-transparent transition-all duration-700 group-hover:w-full" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Linha final da seção: Divisor institucional discreto */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#192338]/10 to-transparent" />
    </section>
  );
};
